/**
 * Utility functions for handling media in the editor
 */

/**
 * Transforms a YouTube URL to an embeddable format
 * @param url YouTube URL
 * @returns Embeddable YouTube URL
 */
export function transformYouTubeUrl(url: string): string {
  // Handle different YouTube URL formats
  const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i;
  const match = url.match(youtubeRegex);
  
  if (match && match[1]) {
    return `https://www.youtube.com/embed/${match[1]}`;
  }
  
  return url;
}

/**
 * Transforms a Vimeo URL to an embeddable format
 * @param url Vimeo URL
 * @returns Embeddable Vimeo URL
 */
export function transformVimeoUrl(url: string): string {
  // Handle different Vimeo URL formats
  const vimeoRegex = /vimeo\.com\/(\d+)/i;
  const match = url.match(vimeoRegex);
  
  if (match && match[1]) {
    return `https://player.vimeo.com/video/${match[1]}`;
  }
  
  return url;
}

/**
 * Detects the type of media from a URL
 * @param url Media URL
 * @returns Media type or null if not recognized
 */
export function detectMediaType(url: string): 'youtube' | 'vimeo' | 'other' {
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    return 'youtube';
  } else if (url.includes('vimeo.com')) {
    return 'vimeo';
  } else {
    return 'other';
  }
}

/**
 * Transforms a media URL to an embeddable format based on its type
 * @param url Media URL
 * @returns Embeddable URL
 */
export function transformMediaUrl(url: string): string {
  const mediaType = detectMediaType(url);
  
  switch (mediaType) {
    case 'youtube':
      return transformYouTubeUrl(url);
    case 'vimeo':
      return transformVimeoUrl(url);
    default:
      return url;
  }
}
